/**
 * SplitResizer — 분할 뷰 드래그 리사이저
 *
 * 좌/우 패널 사이에 배치하여 드래그로 너비 비율을 조절합니다.
 * 부모 컨테이너의 너비를 기준으로 퍼센트를 계산합니다.
 */
import React, { useCallback, useRef, useState } from 'react';
import { GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { t } from '@lingui/macro';
import { cn } from '../../lib/utils';

interface SplitResizerProps {
  /** 현재 왼쪽 패널 비율 (0~1, 예: 0.5 = 50%) */
  ratio: number;
  /** 비율 변경 콜백 */
  onRatioChange: (ratio: number) => void;
  /** 최소 비율 (기본: 0.25). minWidthPx와 동시 적용되며 더 보수적인 쪽이 우선 */
  minRatio?: number;
  /** 최대 비율 (기본: 0.75). minWidthPx와 동시 적용 */
  maxRatio?: number;
  /** 양쪽 패널 모두 보장할 최소 픽셀 폭 (기본: 375 = iPhone 13 mini 가로폭).
      부모 폭 / 2 보다 크면 분할 자체가 불가능하므로 ratio는 0.5에 고정됨 */
  minWidthPx?: number;
  /** 위쪽 버튼 — 왼쪽으로 펼치기(오른쪽 모듈 전체화면). 없으면 버튼 숨김 */
  onExpandLeft?: () => void;
  /** 아래쪽 버튼 — 오른쪽으로 펼치기(왼쪽 채팅 전체화면). 없으면 버튼 숨김 */
  onExpandRight?: () => void;
}

const SplitResizer: React.FC<SplitResizerProps> = ({
  ratio,
  onRatioChange,
  minRatio = 0.25,
  maxRatio = 0.75,
  minWidthPx = 375,
  onExpandLeft,
  onExpandRight,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);

    const parent = containerRef.current?.parentElement;
    if (!parent) return;

    const parentRect = parent.getBoundingClientRect();
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const handlePointerMove = (ev: PointerEvent) => {
      const relX = ev.clientX - parentRect.left;
      let newRatio = relX / parentRect.width;

      // 픽셀 기준 minimum width를 ratio 한계로 변환
      const minRatioByPx = minWidthPx / parentRect.width;
      const maxRatioByPx = 1 - minRatioByPx;

      if (minRatioByPx >= maxRatioByPx) {
        // 부모 폭이 minWidthPx*2 미만 → 분할 불가, 중앙 고정
        newRatio = 0.5;
      } else {
        // 픽셀 제약과 비율 제약 중 더 보수적인 쪽 적용
        const effectiveMin = Math.max(minRatio, minRatioByPx);
        const effectiveMax = Math.min(maxRatio, maxRatioByPx);
        newRatio = Math.max(effectiveMin, Math.min(effectiveMax, newRatio));
      }
      onRatioChange(newRatio);
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [onRatioChange, minRatio, maxRatio, minWidthPx]);

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      className={cn(
        'relative flex items-center justify-center w-0 shrink-0 cursor-col-resize z-10',
        'group select-none touch-none',
      )}
    >
      {/* 히트 영역 (더 넓게) */}
      <div className={cn(
        'absolute inset-y-0 -left-1.5 -right-1.5 flex items-center justify-center',
        'transition-colors duration-150',
        isDragging ? 'bg-primary/8' : 'hover:bg-primary/5',
      )}>
        {/* 시각적 바
            - transform-gpu: GPU 레이어 강제 → sub-pixel 좌표에서도 안정 렌더
            - 드래그 중에는 알파 없는 bg-primary 사용 (알파 40%였을 때 1px 라인이 옅어 사라져 보이는 문제 해소) */}
        <div className={cn(
          'w-px h-full transition-colors duration-150 transform-gpu',
          isDragging ? 'bg-primary' : 'bg-border group-hover:bg-primary/25',
        )} />

        {/* 손잡이 + 펼침 버튼 그룹 (세로: 위=왼쪽으로 펼침 / 손잡이 / 아래=오른쪽으로 펼침) */}
        <div className="absolute top-1/2 -translate-y-1/2 transform-gpu flex flex-col items-center gap-1">
          {/* 위: 왼쪽으로 펼치기 → 오른쪽 모듈 전체화면 */}
          {onExpandLeft && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onExpandLeft(); }}
              title={t`Expand left`}
              className="flex items-center justify-center size-6 rounded-full border bg-background border-border text-muted-foreground hover:border-primary/40 hover:bg-accent hover:text-primary/70 transition-all duration-150 cursor-pointer"
            >
              <ChevronLeft className="size-3.5" />
            </button>
          )}

          {/* 드래그 핸들 */}
          <div className={cn(
            'flex items-center justify-center',
            'size-6 rounded-full border transition-all duration-150',
            isDragging
              ? 'bg-primary/10 border-primary/40 shadow-sm'
              : 'bg-background border-border group-hover:border-primary/30 group-hover:bg-accent',
          )}>
            <GripVertical className={cn(
              'size-3 transition-colors duration-150',
              isDragging ? 'text-primary' : 'text-muted-foreground group-hover:text-primary/60',
            )} />
          </div>

          {/* 아래: 오른쪽으로 펼치기 → 왼쪽 채팅 전체화면 */}
          {onExpandRight && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onExpandRight(); }}
              title={t`Expand right`}
              className="flex items-center justify-center size-6 rounded-full border bg-background border-border text-muted-foreground hover:border-primary/40 hover:bg-accent hover:text-primary/70 transition-all duration-150 cursor-pointer"
            >
              <ChevronRight className="size-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SplitResizer;
